import { type ReactNode, useCallback, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import SegmentedToggle from "~/ui-rtk/components/ui/controls/SegmentedToggle/SegmentedToggle"
import { AggregationType } from "~/ui-rtk/constants/charts"
import {
  REGIONAL_COUNTRY_LABELS,
  REGIONAL_COUNTRY_OPTIONS,
  isRegionalCountry,
  type RegionalCountry,
} from "~/ui-rtk/constants/regional"
import { brandRoutes } from "~/ui-rtk/constants/routes/brand-routes"
import type { KpiConnectParams } from "../connect"
import useRegionalConnect from "../connect-regional"
import { REGIONAL_METRIC_GROUPS } from "../constants/regional"
import { useKPIExport } from "../hooks/useKPIExport"
import { useSynchronizedScroll } from "../hooks/useSynchronizedScroll"
import KPIExportSuccessDialog from "./KPIExportSuccessDialog"
import KPITable from "./KPITable"

type RegionalCountryTablesProps = {
  country: RegionalCountry
  granularity: AggregationType
  showYoYComparison: boolean
  params?: KpiConnectParams
  /** Rendered on the left of the Export row (the country switcher). */
  headerControls: ReactNode
}

// The trimmed regional groups for one country. Owns its own scroll sync,
// expanded state and Export set — none of it is shared with the dashboard's
// tables, which stay mounted on their own view.
function RegionalCountryTables({
  country,
  granularity,
  showYoYComparison,
  params,
  headerControls,
}: RegionalCountryTablesProps) {
  const {
    isLoading,
    isAvailable,
    availabilityLoaded,
    processMetricsData,
    columnConfig,
  } = useRegionalConnect(country, granularity, params)

  const { getScrollRefByKey, handleSyncScroll, handleToggleExpandedWithSync } =
    useSynchronizedScroll()
  const [expandedStates, setExpandedStates] = useState<Record<string, boolean>>(
    {},
  )
  const handleToggleExpanded = useCallback(
    (tableKey: string, isExpanded: boolean) =>
      handleToggleExpandedWithSync(tableKey, isExpanded, (key, expanded) =>
        setExpandedStates(prev => ({ ...prev, [key]: expanded })),
      ),
    [handleToggleExpandedWithSync],
  )

  const groups = useMemo(
    () =>
      REGIONAL_METRIC_GROUPS.map(group => ({
        ...group,
        data: processMetricsData(group.metrics),
      })),
    [processMetricsData],
  )

  const { exportAllTables, showSuccessDialog, handleCloseSuccessDialog } =
    useKPIExport({ columnConfig, granularity })
  const countryLabel = REGIONAL_COUNTRY_LABELS[country]

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        {headerControls}
        <button
          onClick={() =>
            exportAllTables(
              groups.map(group => ({
                key: group.key,
                // The CSV carries no other country marker.
                title: `${countryLabel} ${group.title}`,
                data: group.data,
              })),
            )
          }
          className="flex items-center space-x-2 px-system-lg py-system-sm h-system-2xl bg-system-blue-700 hover:bg-system-blue-600 border border-system-blue-600 rounded-system-md transition-colors"
          title={`Export all ${countryLabel} tables`}
        >
          <svg
            className="w-4 h-4 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
          <span className="text-white text-sm font-medium">Export All</span>
        </button>
      </div>

      {availabilityLoaded && !isAvailable ? (
        <div className="bg-system-blue-800 border border-system-blue-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-2">
            Regional data is not available yet
          </h3>
          <p className="text-system-blue-200 mb-4">
            The country breakout tables are built on the next pipeline run after
            Country Breakout is enabled and the USA / Canada campaign slugs and
            GA4 property countries are set.
          </p>
          <Link
            to={brandRoutes.SETTINGS.COMPANY}
            className="inline-flex items-center px-4 py-2 bg-system-success-600 hover:bg-system-success-700 text-white rounded-lg transition-colors font-medium"
          >
            Company settings
          </Link>
        </div>
      ) : (
        groups.map(group => {
          const scrollRef = getScrollRefByKey(group.key)
          return (
            <div key={group.key} className="mb-8">
              <KPITable
                data={
                  group.data as {
                    key?: string
                    metric?: string
                    type?: string
                    suffix?: string
                    [key: string]: string | number | undefined
                  }[]
                }
                columns={columnConfig}
                title={group.title}
                showTotals={group.showTotals ?? false}
                marginalMetrics={group.marginalMetrics}
                wowMarginalLabel={group.marginalLabel?.[granularity]}
                isExpanded={expandedStates[group.key] ?? true}
                scrollRef={scrollRef}
                onScroll={scrollLeft => handleSyncScroll(scrollLeft, scrollRef)}
                onToggleExpanded={handleToggleExpanded}
                tableKey={group.key}
                showYoYComparison={showYoYComparison}
                isLoading={isLoading}
              />
            </div>
          )
        })
      )}

      <KPIExportSuccessDialog
        isOpen={showSuccessDialog}
        onClose={handleCloseSuccessDialog}
      />
    </>
  )
}

type RegionalKPIProps = {
  granularity: AggregationType
  showYoYComparison: boolean
  /** The page's shared picked range (header picker), if any. */
  params?: KpiConnectParams
}

/**
 * The KPI page's Regional view: US / Canada switcher over the trimmed
 * country-grain metric groups. One country is queried at a time.
 */
export default function RegionalKPI({
  granularity,
  showYoYComparison,
  params,
}: RegionalKPIProps) {
  const [country, setCountry] = useState<RegionalCountry>("USA")

  return (
    <RegionalCountryTables
      country={country}
      granularity={granularity}
      showYoYComparison={showYoYComparison}
      params={params}
      headerControls={
        <SegmentedToggle
          options={REGIONAL_COUNTRY_OPTIONS}
          value={country}
          onChange={value => {
            if (isRegionalCountry(value)) setCountry(value)
          }}
        />
      }
    />
  )
}
